import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { backOr } from "@/app/navigation";
import { useQueryClient } from "@tanstack/react-query";
import { ActionButton, ChatComposer, TopNavigation } from "@hamlak/ui";
import { qk } from "@/api/queryKeys";
import type { InsufficientDetails, Message, ModelId } from "@/api/types";
import { platform } from "@/platform";
import { t } from "@/i18n/ko";
import { useAuthStore } from "@/stores/authStore";
import { onboardingStep } from "@/app/onboarding";
import { useConfigStore } from "@/stores/configStore";
import { useWidthClass } from "@/app/layout/useWidthClass";
import { loginModalState } from "@/app/layout/backdrop";
import { useUiStore, type SheetKind } from "@/stores/uiStore";
import { EmptyState } from "@/components/EmptyState";
import { useCanonicalPlotPath, usePlot } from "@/features/plot/usePlot";
import { ChatHeader } from "./ChatHeader";
import { MessageList } from "./MessageList";
import { useBricks, useConversation, useCurrentModel } from "./useConversation";
import { useChatSettings } from "./useChatSettings";
import { useSuggestions } from "./useSuggestions";
import { useHeaderScrollBg } from "./useHeaderScrollBg";
import { useTurnStream } from "./useTurnStream";
import { useRerollStream } from "./useRerollStream";
import { ModelSheet } from "./sheets/ModelSheet";
import { MenuSheet } from "./sheets/MenuSheet";
import { useOlderMessages } from "./useOlderMessages";
import { LeaveDialog } from "@/features/conversations/LeaveDialog";
import { useForgetConversation, useLeaveConversation } from "@/features/conversations/useLeaveConversation";
import { InsufficientSheet } from "./sheets/InsufficientSheet";
import { RerollSheet } from "./sheets/RerollSheet";
import { openSupportForm } from "@/features/settings/supportForm";
import { analytics } from "@/analytics";
import { CopySheet } from "./sheets/CopySheet";
import { ReportScreen } from "@/features/report/ReportScreen";
import type { LongPressTarget } from "./CharacterTurn";
import "./chat.css";

export type LoginTrigger =
  "focus" | "preset" | "send" | "suggest" | "menu";

const MAX_TEXT = 1000;

const NO_MESSAGES: readonly Message[] = [];

/** 엔터 전송은 웹 데스크톱(마우스·트랙패드)만. 앱·터치 기기는 엔터 줄바꿈(AC0-231 #15, AC0-284). */
function prefersEnterToSend(): boolean {
  if (platform.clientKind !== "web" || typeof matchMedia !== "function") return false;
  return matchMedia("(hover: hover) and (pointer: fine)").matches;
}

/**
 * /chat/:key — key 는 slug(예전 UUID 주소는 slug 로 바꾼다, AC0-161). 대화·초안·분석은 로드된 plot.id 로.
 * - 비회원(F01-12): 고지·첫 장면·프리셋·입력부는 보이되 모든 상호작용이 로그인 시트(서버 턴 호출 없음).
 * - 회원(F03-03~05·18, F04-01·07): GET /conversations 메시지 + useTurnStream SSE + 모델/도구/메뉴/부족/복사 시트 + 추천 답변.
 * - 답변 다시 생성(F03-20~23): 마지막 답변 ↻ → RerollSheet(방향·가격) → useRerollStream. 생성 중에는 전송·추천·↻ 잠김.
 */
export function ChatPage() {
  const { key = "" } = useParams<{ key: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const plot = usePlot(key);
  useCanonicalPlotPath(key, plot, "/chat");
  const plotId = plot.data?.id ?? "";
  const slug = plot.data?.slug ?? key;
  const location = useLocation();
  const isLarge = useWidthClass() === "large";
  // 온보딩 미완 세션은 회원이 아니다(account-deletion 결정 10): 셸이 로그아웃시키기 전 첫 렌더에도 대화 API 를 부르지 않는다(AC0-149)
  const isMember = useAuthStore((s) => s.status === "member" && onboardingStep(s.user) === null);
  // 직접 진입이면 회원은 대화 목록, 비회원은 홈으로
  const back = () => backOr(navigate, isMember ? "/chats" : "/home");
  const balance = useAuthStore((s) => s.balance);
  const hidePresets = useAuthStore((s) => s.settings?.hidePresets ?? false);
  const pricing = useConfigStore((s) => s.settings?.pricing);
  const draft = useUiStore((s) => s.draft[plotId] ?? "");
  const setDraft = useUiStore((s) => s.setDraft);
  const requestLogin = useUiStore((s) => s.requestLogin);
  const showSnackbar = useUiStore((s) => s.showSnackbar);
  const sheets = useUiStore((s) => s.sheets);
  const openSheet = useUiStore((s) => s.openSheet);
  const closeSheet = useUiStore((s) => s.closeSheet);

  const conversation = useConversation(plotId, isMember);
  const [leaveOpen, setLeaveOpen] = useState(false);
  const leave = useLeaveConversation();
  const forget = useForgetConversation();
  const older = useOlderMessages(conversation.data?.id, conversation.data?.messages ?? NO_MESSAGES);
  useBricks(isMember);
  const currentModel = useCurrentModel(conversation.data?.model);
  const settings = useChatSettings(key, plotId);

  const [insufficient, setInsufficient] = useState<
    (InsufficientDetails & { kind: "turn" | "suggestion" | "reroll" }) | null
  >(null);
  const [pressTarget, setPressTarget] = useState<LongPressTarget | null>(null);
  /** 신고 대상 턴(= 캐릭터 메시지 id). ⋯ 메뉴는 마지막 캐릭터 메시지(last), 길게 누르기는 그 메시지. */
  const [reportTurn, setReportTurn] = useState<{ id: string; last: boolean } | null>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const pageRef = useRef<HTMLElement>(null);
  useHeaderScrollBg(pageRef);

  const onTurnInsufficient = useCallback(
    (d: InsufficientDetails) => {
      setInsufficient({ ...d, kind: "turn" });
      if (currentModel) analytics.track("view_insufficient_brick", { chat_model: currentModel.id });
      openSheet("insufficient");
    },
    [openSheet, currentModel],
  );
  const onSuggestInsufficient = useCallback(
    (d: InsufficientDetails) => {
      setInsufficient({ ...d, kind: "suggestion" });
      if (currentModel) analytics.track("view_insufficient_brick", { chat_model: currentModel.id });
      openSheet("insufficient");
    },
    [openSheet, currentModel],
  );


  const turn = useTurnStream({
    plotId,
    conversationId: conversation.data?.id,
    onInsufficient: onTurnInsufficient,
  });
  const onRerollInsufficient = useCallback(
    (d: InsufficientDetails) => {
      setInsufficient({ ...d, kind: "reroll" });
      if (currentModel) analytics.track("view_insufficient_brick", { chat_model: currentModel.id });
      openSheet("insufficient");
    },
    [openSheet, currentModel],
  );
  const reroll = useRerollStream({
    plotId,
    conversationId: conversation.data?.id,
    onInsufficient: onRerollInsufficient,
  });
  /** ↻ 를 누른 답변 id. 시트에서 [다시 생성]을 누르면 이 답변을 교체한다 */
  const [rerollTarget, setRerollTarget] = useState<string | null>(null);
  const suggestions = useSuggestions({
    conversationId: conversation.data?.id,
    onInsufficient: onSuggestInsufficient,
  });

  // 0.5 백그라운드 복귀: bricks·conversation invalidate, 진행 중 SSE는 recovering.
  useEffect(() => {
    if (!isMember) return;
    return platform.app.onForeground(() => {
      void qc.invalidateQueries({ queryKey: qk.bricks });
      void qc.invalidateQueries({ queryKey: qk.conversation(plotId) });
      turn.recoverNow();
    });
  }, [isMember, plotId, qc, turn.recoverNow, turn]);

  const requireLogin = (_trigger: LoginTrigger) => {
    // 비회원은 입력창 탭이 곧 전송 시도라(AC0-94) 트리거와 무관하게 로그인 화면으로 보내는 순간을 센다
    analytics.track("view_login_prompt", { plot_id: slug });
    requestLogin({
      returnTo: `/chat/${encodeURIComponent(slug)}`,
      ...(plot.data ? { plot: { plotName: plot.data.name, coverUrl: plot.data.coverUrl, tagline: plot.data.tagline } } : {}),
    });
    navigate("/login", { state: loginModalState(location) });
  };
  const guard = (trigger: LoginTrigger, memberAction: () => void) => () => {
    if (!isMember) return requireLogin(trigger);
    memberAction();
  };
  const isOpen = (k: SheetKind) => sheets.includes(k);
  const closeAll = () => {
    for (const k of ["menu", "model", "insufficient"] as const)
      closeSheet(k);
  };

  const sendText = (text: string, model?: ModelId) => {
    const m = pricing?.models.find((x) => x.id === model) ?? currentModel;
    if (!m) return;
    const trimmed = text.trim().slice(0, MAX_TEXT);
    if (!trimmed) return;
    if (model && model !== currentModel?.id)
      settings.setConversationModel(model);
    if (turn.send(trimmed, m.id, m.costPerTurn)) {
      setDraft(plotId, "");
      suggestions.clear();
    }
  };
  const startReroll = (direction: string) => {
    const m = currentModel;
    closeSheet("reroll");
    if (!m || !rerollTarget) return;
    if (reroll.reroll({ messageId: rerollTarget, model: m.id, cost: m.rerollCost, direction })) suggestions.clear(); // 추천 칩은 이전 답변 기준이라 비운다
  };
  const retryTurn = (model?: ModelId) => {
    const m = pricing?.models.find((x) => x.id === model) ?? currentModel;
    if (!m) return;
    if (model && model !== currentModel?.id)
      settings.setConversationModel(model);
    turn.retry(m.costPerTurn, m.id);
  };

  const copyText = async () => {
    const text = pressTarget?.text;
    setPressTarget(null);
    if (!text) return;
    try {
      await platform.device.copy(text);
      showSnackbar(t("chat.copied"));
    } catch {
      showSnackbar(t("chat.copyFailed"));
    }
  };

  /** 1.21 신고 화면 열기. 대상이 없으면(완료된 캐릭터 메시지 없음) 토스트. */
  const openReport = (turnId: string | null, last: boolean) => {
    if (!turnId) {
      showSnackbar(t("report.noTarget"));
      return;
    }
    setReportTurn({ id: turnId, last });
    openSheet("report");
  };
  const lastCharacterTurnId = () => {
    const msgs = conversation.data?.messages ?? [];
    for (let i = msgs.length - 1; i >= 0; i--) {
      const m = msgs[i]!;
      if (m.role === "character" && m.status === "complete") return m.id;
    }
    return null;
  };

  if (plot.isPending) {
    return (
      <main className="page chat">
        <TopNavigation title="" right={null} onBack={back} />
        <div className="page-placeholder" role="status">
          {t("common.loading")}
        </div>
      </main>
    );
  }
  if (plot.isError || !plot.data) {
    return (
      <main className="page chat">
        <TopNavigation title="" right={null} onBack={back} />
        <EmptyState
          message={t("plot.notFound")}
          action={{
            label: t("common.retry"),
            onClick: () => void plot.refetch(),
          }}
        />
      </main>
    );
  }

  const p = plot.data;
  const conv = conversation.data;
  // 프리셋: 회원은 conversation.presets(null = 숨김), 비회원은 플롯 presets. 시안(F01-12·F03-03): 첫 메시지 전에만 칩 행, 그 뒤엔 점선 추천 버튼 행 — 둘은 배타.
  const presets = isMember ? (conv ? (conv.presets ?? []) : []) : p.presets;
  const beforeFirstMessage = (conv?.messages.length ?? 0) === 0;
  const chips =
    suggestions.items.length > 0
      ? suggestions.items
      : beforeFirstMessage
        ? presets
        : [];
  const composerDisabled = isMember && (turn.busy || reroll.busy || !conv);
  const sideSub = [p.character.age !== null ? t("plot.age", { age: p.character.age }) : null, p.character.job].filter(Boolean).join(" · ");

  return (
    <main ref={pageRef} className="page page--wide chat" data-testid="chat-page">
      {isLarge && (
        /* 배경 커버는 PC(596:4730)만. 모바일 시안은 단색(1차 숨김 2차 요소, docs/phase1-spec.md:190) */
        <div className="chat__bg" aria-hidden>
          <img
            src={p.coverUrl}
            alt=""
            onError={(e) => (e.currentTarget.style.visibility = "hidden")}
          />
        </div>
      )}
      {isLarge && (
        /* PC(596:4714): 왼쪽 캐릭터 패널 330 — 대표 이미지 3:4(자르지 않고 흐림 배경) · 이름 · 나이·직업 · 태그라인 · 플롯 프로필 보기 */
        <aside className="chat__side" aria-label={p.character.name}>
          <div className="chat__side-cover">
            <img src={p.coverUrl} alt="" aria-hidden className="chat__side-cover-bg" />
            <img src={p.coverUrl} alt="" className="chat__side-cover-img" />
          </div>
          <p className="chat__side-name">{p.character.name}</p>
          {sideSub && <p className="chat__side-sub">{sideSub}</p>}
          <p className="chat__side-tagline">{p.tagline}</p>
          <ActionButton variant="neutralWeak" size="medium" block className="chat__side-btn" onClick={() => navigate(`/plots/${encodeURIComponent(slug)}`, { state: { fromChat: true } })}>
            {t("chat.menu.plot")}
          </ActionButton>
        </aside>
      )}
      <div className="chat__main">
        <ChatHeader
          name={p.character.name}
          avatarUrl={p.character.images[0] ?? p.coverUrl}
          onBack={back}
          onMenu={guard("menu", () => openSheet("menu"))}
        />
        {isMember && conversation.isError && (
          <div className="chat__notice">
            <EmptyState
              message={t("chat.loadError")}
              action={{
                label: t("common.retry"),
                onClick: () => void conversation.refetch(),
              }}
            />
          </div>
        )}
        <MessageList
          firstScene={p.firstScene}
          speaker={{ name: p.character.name, avatarUrl: p.character.images[0] ?? p.coverUrl }}
          messages={older.messages}
          older={{ hasMore: older.hasMore, loading: older.loading, load: () => void older.loadOlder() }}
          turn={isMember ? turn.state : undefined}
          onRetry={() => retryTurn()}
          onFocusInput={() => {
            turn.dismiss();
            reroll.dismiss();
            composerRef.current?.querySelector("textarea")?.focus();
          }}
          onLongPress={isMember ? (target) => setPressTarget(target) : undefined}
          {...(isMember
            ? {
                reroll: reroll.state,
                onReroll: (id: string) => {
                  reroll.dismiss();
                  setRerollTarget(id);
                  openSheet("reroll");
                },
                onRerollRetry: () => reroll.retry(currentModel?.rerollCost ?? 0),
              }
            : {})}
        />
        <div className="chat__bottom" ref={composerRef}>
          {/* 시안 ChatComposer 1차 조합: 메타 행(모델·단가) 없음. 칩 행(프리셋/추천 결과)과 점선 추천 버튼은 배타. ✨ 버튼은 회원만(F01-12 비회원 첫 장면엔 없음)이고 ⋯ 와 같은 메뉴 시트를 연다(AC0-231 — 도구 시트 폐지).
              프리셋 숨김(F03-18)이면 점선 추천 버튼도 없다(AC0-237) */}
          <ChatComposer
            value={draft}
            onValueChange={(v) => setDraft(plotId, v.slice(0, MAX_TEXT))}
            onSend={guard("send", () => sendText(draft))}
            {...(isMember
              ? { onToolsClick: () => openSheet("menu") }
              : { onFocus: () => requireLogin("focus") })}
            enterToSend={prefersEnterToSend()}
            disabled={composerDisabled}
            placeholder={t("chat.placeholder")}
            {...(chips.length > 0
              ? {
                  suggestions: chips,
                  onSuggestionClick: (text: string) =>
                    isMember ? setDraft(plotId, text) : requireLogin("preset"),
                }
              : {})}
            {...(pricing && chips.length === 0 && !hidePresets
              ? {
                  suggestAction: {
                    label: t("chat.suggest", { cost: pricing.suggestion }),
                    onClick: guard("suggest", () => void suggestions.request()),
                    disabled: isMember && (suggestions.pending || turn.busy || reroll.busy),
                  },
                }
              : {})}
          />
        </div>
      </div>

      {isMember && (
        <>
          <ModelSheet
            open={isOpen("model")}
            models={pricing?.models ?? []}
            current={currentModel?.id}
            balance={balance}
            onApply={(m) => {
              settings.setConversationModel(m);
              closeSheet("model");
            }}
            onClose={() => closeSheet("model")}
          />
          <MenuSheet
            open={isOpen("menu")}
            profile={{
              name: p.name,
              avatarUrl: p.character.images[0] ?? p.coverUrl,
              subtitle: currentModel ? `${p.character.name} · ${currentModel.name}` : p.character.name,
            }}
            hidePresets={hidePresets}
            onModel={() => {
              closeSheet("menu");
              openSheet("model");
            }}
            onHidePresets={(hide) => settings.setHidePresets(hide)}
            onPlot={() => {
              closeSheet("menu");
              navigate(`/plots/${encodeURIComponent(slug)}`, { state: { fromChat: true } });
            }}
            onFeedback={() => {
              closeSheet("menu");
              void openSupportForm(useAuthStore.getState().user?.publicId ?? null);
            }}
            onReport={() => {
              closeSheet("menu");
              openReport(lastCharacterTurnId(), true);
            }}
            onLeave={
              conv
                ? () => {
                    closeSheet("menu");
                    setLeaveOpen(true);
                  }
                : undefined
            }
            onClose={() => closeSheet("menu")}
          />
          <LeaveDialog
            open={leaveOpen}
            inactive={p.status === "inactive"}
            loading={leave.isPending}
            onCancel={() => setLeaveOpen(false)}
            onConfirm={() => {
              if (!conv) return;
              leave.mutate(
                { conversationId: conv.id, plotId },
                {
                  // 캐시는 이 화면이 내려간 뒤(다음 틱) 지운다. 구독이 남은 채로 지우면 get-or-create 재조회로 새 대화가 생긴다
                  onSuccess: async () => {
                    setLeaveOpen(false);
                    await navigate("/chats", { replace: true });
                    setTimeout(() => forget(plotId), 0);
                  },
                  onError: () => setLeaveOpen(false),
                },
              );
            }}
          />
          <ReportScreen
            open={isOpen("report")}
            target={
              conv && reportTurn
                ? {
                    kind: "turn",
                    conversationId: conv.id,
                    turnId: reportTurn.id,
                    label: t(reportTurn.last ? "report.target.last" : "report.target.selected", { name: p.character.name }),
                  }
                : null
            }
            onClose={() => closeSheet("report")}
          />
          <RerollSheet
            open={isOpen("reroll")}
            cost={currentModel?.rerollCost ?? 0}
            onSubmit={startReroll}
            onClose={() => closeSheet("reroll")}
          />
          <InsufficientSheet
            open={isOpen("insufficient") && insufficient !== null}
            details={insufficient}
            onContinueWith={
              insufficient?.kind === "turn"
                ? (m) => (turn.state.text ? retryTurn(m) : sendText(draft, m))
                : undefined
            }
            onClose={() => {
              closeAll();
              setInsufficient(null);
            }}
          />
          <CopySheet
            open={pressTarget !== null}
            onCopy={() => void copyText()}
            onReport={
              pressTarget?.role === "character" && pressTarget.messageId
                ? () => {
                    const id = pressTarget.messageId ?? null;
                    setPressTarget(null);
                    openReport(id, id === lastCharacterTurnId());
                  }
                : undefined
            }
            onClose={() => setPressTarget(null)}
          />
        </>
      )}
    </main>
  );
}
